import { computed, DestroyRef, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TranslateService } from '@ngx-translate/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationsHttpService } from '@app/shared/services/http/locations-http.service';
import { Location } from '@app/shared/types/models/location/location.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { concatMap, EMPTY, from, Observable } from 'rxjs';
import { finalize, map, tap, toArray } from 'rxjs/operators';
import { ActiveProjectService } from './active-project.service';
import { DataService } from './data-service';
import { LocationDTO } from './dtos/location/location.dto';
import { LocationFactory } from './factories/location.factory';

/**
 * LocationsDataService - Data access and store management for locations.
 *
 * This service manages location data in the store, provides queries and getters,
 * and handles CRUD operations with automatic store synchronization.
 *
 * When {@link ActiveProjectService.activeProjectId} changes, fetches that project’s location tree (constructor
 * `effect` with {@link takeUntilDestroyed} on the subscription).
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by location components and actions service to access and modify location data.
 *
 * Architecture:
 * - {@link LocationsActionsService}: User actions, mutations, side effects (toasts, modals)
 * - {@link LocationsDataService}: Data access, queries, getters, store management
 * - {@link ActiveProjectService}: Triggers project location fetch for the active project
 * - {@link LocationsHttpService}: HTTP API calls
 */
@Injectable({ providedIn: 'root' })
export class LocationsDataService extends DataService<LocationDTO, Location> {
  private readonly locations: DataStore<Location> = new DataStore<Location>();
  private readonly locationsByParent: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly projectLocations: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly locationFactory: LocationFactory = inject(LocationFactory);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly locationsHttpService: LocationsHttpService = inject(LocationsHttpService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public constructor() {
    super();
    effect(() => {
      const projectId = this.activeProjectService.activeProjectId();
      if (!projectId) {
        return;
      }

      this.fetchProjectLocations(projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    });
  }

  public getById(id: string): Signal<Location | undefined> {
    return computed(() => this.locations.get(id)());
  }

  public getLocationsByParent(parentId: string): Signal<Location[] | undefined> {
    if (!parentId) {
      return signal(undefined);
    }

    return computed(
      () =>
        (this.locationsByParent.get(parentId)()?.ids ?? [])
          .map(id => this.getById(id)())
          .sort((a, b) => a.orderNo - b.orderNo) || []
    );
  }

  public getAllSubLocations(parentId: string): Location[] {
    const direct = this.getLocationsByParent(parentId)() || [];
    const nested = direct.flatMap(loc => (loc?.id ? this.getAllSubLocations(loc.id) : []));
    return [...direct, ...nested];
  }

  public getProjectLocations(projectId: string): Signal<Location[] | undefined> {
    if (!projectId) {
      return signal(undefined);
    }

    return computed(
      () =>
        (this.projectLocations.get(projectId)()?.ids ?? [])
          .map(id => this.getById(id)())
          .sort((a, b) => a.orderNo - b.orderNo) || []
    );
  }

  public addMultipleLocations(
    projectId: string,
    locations: { name: string; orderNo: number; parentLocationId?: string }[],
    displayName: string
  ): Observable<LocationDTO[]> {
    if (locations.length === 0) {
      return EMPTY;
    }

    this.loaderService.startLoading();
    return from(locations).pipe(
      concatMap(location =>
        this.locationsHttpService.addLocation(projectId, location.name, location.orderNo, location.parentLocationId)
      ),
      toArray(),
      map(dtos => [...dtos].sort((first, second) => first.orderNo - second.orderNo)),
      tap({
        next: sortedDtos => {
          this.upsertMultipleLocations(projectId, sortedDtos);
          this.toastService.success(
            this.translateService.instant('project-locations.toasts.multiple-successfully-added', {
              name: displayName
            })
          );
        }
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public addLocation(
    projectId: string,
    name: string,
    orderNo: number,
    parentLocationId?: string
  ): Observable<LocationDTO> {
    this.loaderService.startLoading();
    return this.locationsHttpService.addLocation(projectId, name, orderNo, parentLocationId).pipe(
      tap({
        next: dto => {
          this.upsertLocalData({ ...dto, childrenCount: 0 });
          if (dto.parentId) {
            this.locationsByParent.upsert({
              id: dto.parentId,
              ids: (this.locationsByParent.get(dto.parentId)()?.ids ?? []).concat(dto.id)
            });
          } else {
            this.projectLocations.upsert({
              id: projectId,
              ids: (this.projectLocations.get(projectId)()?.ids ?? []).concat(dto.id)
            });
          }

          this.toastService.success(
            this.translateService.instant('project-locations.toasts.successfully-added', {
              name: name
            })
          );
        }
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public duplicateLocations(
    projectId: string,
    locationIds: string[],
    newParentId: string,
    includeAttachments = false,
    duplicateChildren = false
  ): Observable<LocationDTO[]> {
    this.loaderService.startLoading();
    return this.locationsHttpService
      .duplicateLocations(projectId, locationIds, newParentId, includeAttachments, duplicateChildren)
      .pipe(
        tap({
          next: locations => {
            locations.forEach(location => {
              this.upsertLocalData(location);
              const parentId = location.parentId ?? newParentId;
              if (parentId) {
                const existing = this.locationsByParent.get(parentId)?.() ?? { id: parentId, ids: [] };
                this.locationsByParent.upsert({
                  id: parentId,
                  ids: [...(existing.ids ?? []), location.id]
                });
              } else {
                this.projectLocations.upsert({
                  id: projectId,
                  ids: (this.projectLocations.get(projectId)()?.ids ?? []).concat(location.id)
                });
              }
            });
            this.toastService.success(
              this.translateService.instant('project-locations.toasts.successfully-duplicated')
            );
          }
        }),
        finalize(() => this.loaderService.stopLoading())
      );
  }

  public updateLocation(
    projectId: string,
    id: string,
    name: string,
    orderNo: number,
    parentLocationId?: string
  ): Observable<LocationDTO> {
    const currentLocation = this.locations.get(id)();
    const currentParentId = currentLocation?.parentLocation()?.id;

    if (currentLocation) {
      currentLocation.orderNo = orderNo;
      currentLocation.name = name;
    }

    this.loaderService.startLoading();
    return this.locationsHttpService.updateLocation(projectId, id, name, orderNo, parentLocationId).pipe(
      tap({
        next: dto => {
          if (currentParentId !== parentLocationId) {
            if (currentParentId) {
              this.locationsByParent.upsert({
                id: currentParentId,
                ids: (this.locationsByParent.get(currentParentId)()?.ids ?? []).filter(i => i !== id)
              });
            } else {
              this.projectLocations.upsert({
                id: projectId,
                ids: (this.projectLocations.get(projectId)()?.ids ?? []).filter(i => i !== id)
              });
            }
            if (parentLocationId) {
              this.locationsByParent.upsert({
                id: parentLocationId,
                ids: (this.locationsByParent.get(parentLocationId)()?.ids ?? []).concat(id)
              });
            } else {
              this.projectLocations.upsert({
                id: projectId,
                ids: (this.projectLocations.get(projectId)()?.ids ?? []).concat(id)
              });
            }
          }
          this.upsertLocalData(dto);

          this.toastService.success(
            this.translateService.instant('project-locations.toasts.successfully-updated', {
              name: name
            })
          );
        }
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public deleteLocations(projectId: string, locations: Location[]): Observable<string> {
    return this.locationsHttpService.deleteLocations(projectId, locations).pipe(
      tap({
        next: () => {
          const rootLocations = locations.filter(l => !l.parentLocation()?.id);
          rootLocations.forEach(location =>
            this.projectLocations.upsert({
              id: projectId,
              ids: (this.projectLocations.get(projectId)()?.ids ?? []).filter(id => id !== location.id)
            })
          );
          const childLocations = locations.filter(l => l.parentLocation() !== undefined);
          childLocations.forEach(location =>
            this.locationsByParent.upsert({
              id: location.parentLocation().id,
              ids: (this.locationsByParent.get(location.parentLocation().id)()?.ids ?? []).filter(
                id => id !== location.id
              )
            })
          );
          locations.forEach(location => this.locations.delete(location.id));
        }
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public reorderLocations(projectId: string, ids: string[]): Observable<LocationDTO[]> {
    this.loaderService.startLoading();
    return this.locationsHttpService.reorderLocations(projectId, ids).pipe(
      tap({
        next: (dtos: LocationDTO[]) => {
          dtos.forEach(dto => this.upsertLocalData(dto));
        }
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public loadProjectLocations(projectId: string): Observable<LocationDTO[]> {
    if (!projectId) {
      return EMPTY;
    }

    if (this.projectLocations?.hasDataForId(projectId)) {
      return EMPTY;
    }

    return this.fetchProjectLocations(projectId);
  }

  public upsertMultipleLocations(projectId: string, dtos: LocationDTO[]): void {
    const sortedDtos = [...dtos].sort((first, second) => first.orderNo - second.orderNo);
    const newIds = sortedDtos.map(dto => dto.id);
    const parentId = sortedDtos[0]?.parentId;

    sortedDtos.forEach(dto => this.upsertLocalData({ ...dto, childrenCount: 0 }));

    if (parentId) {
      const existingIds = this.locationsByParent.get(parentId)()?.ids ?? [];
      this.locationsByParent.upsert({
        id: parentId,
        ids: [...existingIds.filter(id => !newIds.includes(id)), ...newIds]
      });
      return;
    }

    const existingIds = this.projectLocations.get(projectId)()?.ids ?? [];
    this.projectLocations.upsert({
      id: projectId,
      ids: [...existingIds.filter(id => !newIds.includes(id)), ...newIds]
    });
  }

  public upsertLocalData(dto: LocationDTO): Signal<Location> {
    if (!dto) {
      return signal(undefined);
    }

    const location = this.locationFactory.produce(dto);
    location.parentLocation = computed(() => (dto.parentId ? this.getById(dto.parentId)() : undefined));
    location.childrenCount = computed(() => this.locationsByParent.get(location.id)()?.ids?.length);

    return this.locations.upsert(location);
  }

  protected fetchProjectLocations(projectId: string): Observable<LocationDTO[]> {
    this.loaderService.startLoading();
    return this.locationsHttpService.getProjectLocations(projectId).pipe(
      tap({
        next: (locations: LocationDTO[]) => {
          const rootLocations = locations.filter(l => !l.parentId);
          this.projectLocations.upsert({
            id: projectId,
            ids: locations.filter(location => !location.parentId).map(location => location.id)
          });
          rootLocations.forEach(location => this.upsertLocationsByParent(location, locations));

          rootLocations.forEach(location => this.updateLocationsByParent(location, locations));
        }
      }),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  private upsertLocationsByParent(parentLocation: LocationDTO, locations: LocationDTO[]): void {
    if (!parentLocation) {
      return;
    }
    this.upsertLocalData(parentLocation);
    const children = locations.filter(l => l.parentId === parentLocation.id) ?? [];
    children.forEach(child => this.upsertLocationsByParent(child, locations));
  }

  private updateLocationsByParent(parentLocation: LocationDTO, locations: LocationDTO[]): void {
    if (!parentLocation) {
      return;
    }

    const children = locations.filter(l => l.parentId === parentLocation.id) ?? [];
    this.locationsByParent.upsert({ id: parentLocation.id, ids: children.map(l => l.id) });
    children.forEach(child => this.updateLocationsByParent(child, locations));
  }
}
